'use client'

import { faList, faTableCells } from '@fortawesome/pro-solid-svg-icons'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  useAnalyticsContext,
} from '@b-stock/fe-core/bstock-react'
import type { StoreFrontOptionsDefaultViewEnum } from '@b-stock/listing-api-client'

import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import {
  getDefaultSort,
  LISTING_SEARCH_DEFAULT_PAGE_SIZE,
} from '@/components/listings/search/ListingSearchProvider/defaultState'
import { readRememberedPageSize } from '@/components/listings/search/ListingSearchProvider/rememberedPageSize'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { FiltersButton, FiltersIcon } from '../shared/listings-ui'

const Body = styled.div`
  display: flex;
  gap: 0.75rem;
`

export const ViewToggle = () => {
  const { formatMessage } = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const { screenName } = useAnalyticsContext()
  const { isPageSizeRemembered, setPageSize, setSort, setView, state } =
    useListingSearch()

  const trackClick = (buttonName: string) => {
    trackButtonClicked({
      screenName,
      buttonName,
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
    })
  }

  /**
   * Always reset page size and sort to the new view's defaults; a remembered
   * page size stands in for the default page size.
   */
  const switchToView = (newView: StoreFrontOptionsDefaultViewEnum) => {
    setView(newView)
    setPageSize(
      (isPageSizeRemembered ? readRememberedPageSize() : null) ??
        LISTING_SEARCH_DEFAULT_PAGE_SIZE[newView]
    )
    setSort(
      ...getDefaultSort({
        hasQuery: !!state.filters.q,
        view: newView,
      })
    )
  }

  return (
    <Body>
      <FiltersButton
        active={state.view === 'GRID'}
        appearance="secondary"
        onClick={() => {
          if (state.view === 'GRID') return
          trackClick('grid_view')
          switchToView('GRID')
        }}
        title={formatMessage({
          id: 'Listings.ListView.viewToggle.toGridView',
        })}
      >
        <FiltersIcon icon={faTableCells} />
      </FiltersButton>
      <FiltersButton
        active={state.view === 'LIST'}
        appearance="secondary"
        onClick={() => {
          if (state.view === 'LIST') return
          trackClick('list_view')
          switchToView('LIST')
        }}
        title={formatMessage({
          id: 'Listings.ListView.viewToggle.toListView',
        })}
      >
        <FiltersIcon icon={faList} />
      </FiltersButton>
    </Body>
  )
}
